import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tv2 } from "lucide-react";
import { useSelectedShow, ALL_SHOWS } from "@/hooks/useSelectedShow";

/**
 * The show picker.
 *
 * Lives on the Shows/Manage screen, not Home: switching shows is rare, and an
 * always visible control there made an accidental tap easy and costly, since
 * a wrong show silently misfiles every contact captured after it. Home only
 * shows the current show's name, with a link to get here to change it.
 *
 * `allowAll` decides whether "All shows" is on the menu. The Shows screen
 * passes it so the contact list can span every show. The capture screen does
 * not, because a contact has to be filed into exactly one show, and there it
 * falls back to showing the real show a capture would land in, so the rep can
 * see and change it.
 *
 * allowAll also switches to a visually prominent style, task: prominent show
 * picker, 2026-09-20: the owner's own report was that this control was too
 * small and easy to miss specifically on the Shows screen. Deliberately
 * scoped to allowAll rather than changed everywhere this component is used:
 * the capture screen's own version (NewMeeting.jsx, allowAll false) keeps its
 * existing compact card look untouched, since nothing was reported wrong
 * with it there and the owner's own ask named the Shows page specifically.
 */
export default function ShowSelector({ allowAll = false }) {
  const { shows, selectedShowId, setSelectedShowId, selectedShow, isAllShows } = useSelectedShow();

  // With "All shows" active but not offered here, show the show a capture
  // would actually go into rather than a blank or an invalid selection.
  const value = allowAll ? selectedShowId : (selectedShow?.id ?? '');

  if (allowAll) {
    return (
      <Select value={value} onValueChange={setSelectedShowId}>
        {/* Matches the "+ New Contact" button's own look (HomeHeader.jsx):
            solid bg-accent fill, white bold text, same rounded-lg radius.
            h-auto/whitespace-normal/[&>span]:line-clamp-none, not the shared
            SelectTrigger's own defaults (a fixed h-9 and a 1-line clamp on
            its value span): a show's name can run long enough that even
            full width does not fit on one line, and this must wrap to a
            second line rather than silently cutting it off, which a fixed
            height or a line-clamp would do instead. [&_svg]:opacity-100
            overrides the chevron's own hardcoded opacity-50 (ui/select.jsx)
            by CSS specificity (a descendant selector beats a single class),
            since that icon's className is not exposed as a prop to
            override any other way; its color already follows
            text-accent-foreground (white) via the normal currentColor
            inheritance every lucide icon here already uses, so only the
            opacity needed overriding to read as a real, fully white
            chevron rather than a faded one. */}
        <SelectTrigger className="h-auto w-full items-center justify-between gap-2 whitespace-normal rounded-lg border-0 bg-accent px-4 py-2.5 text-left text-sm font-semibold text-accent-foreground shadow-sm focus:ring-0 focus-visible:ring-0 [&>span]:line-clamp-none [&_svg]:opacity-100">
          <SelectValue placeholder={shows.length ? 'Select event...' : 'No events assigned'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SHOWS}>All shows</SelectItem>
          {shows.map(s => (
            <SelectItem key={s.id} value={s.id}>
              {s.name}{s.location ? ` · ${s.location}` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5 bg-card border border-border rounded-xl">
      <Tv2 className="w-4 h-4 text-accent shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground font-medium">
          Current Event
        </p>
        <Select value={value} onValueChange={setSelectedShowId}>
          <SelectTrigger className="h-6 text-sm font-semibold border-0 bg-transparent p-0 focus:ring-0 focus-visible:ring-0 shadow-none">
            <SelectValue placeholder={shows.length ? 'Select event...' : 'No events assigned'} />
          </SelectTrigger>
          <SelectContent>
            {shows.map(s => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}{s.location ? ` · ${s.location}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
